'use client'

import Image from 'next/image'

import {
  DesktopDemoVideoThumbnail,
  MobileDemoVideoThumbnail,
} from '~/app/_root/DemoVideoThumbnail'
import { SplitsLink } from '~/components/Link'
import { LoginForm } from '~/components/LoginForm'
import { Message } from '~/components/component-library'
import { useIsStandalone } from '~/hooks/useIsStandalone'
import { useReferralCode } from '~/hooks/useReferralCode'
import { CheckCircle } from '~/icons/CheckCircle'

const features = [
  {
    title: 'Instant setup',
    description:
      'Sign up in 60 seconds using just your email. No paperwork or entity needed.',
  },
  {
    title: 'Cleaner books',
    description:
      'Separate accounts for separate purposes. Exportable records for everything.',
  },
  {
    title: 'Faster workflows',
    description:
      'Deposit, pay, invoice, swap, bridge—all your ops in one place with complete visibility.',
  },
]

// Partner logos - these SVGs are black, invert them in dark mode
const partnerLogos = [
  {
    name: 'ETH Global',
    src: '/partners/partner_ethglobal.svg',
    url: 'https://ethglobal.com/',
  },
  {
    name: 'Daimo',
    src: '/partners/partner_daimo.svg',
    url: 'https://daimo.com/',
  },
  {
    name: 'Clanker',
    src: '/partners/partner_clanker.svg',
    url: 'https://clanker.world/',
  },
  {
    name: 'Indexing',
    src: '/partners/partner_indexingco.svg',
    url: 'https://www.indexing.co/',
  },
  {
    name: 'Syndicate',
    src: '/partners/syndicate.svg',
    url: 'https://syndicate.io/',
  },
]

function FeatureItem({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3 text-gray-900 dark:text-white">
        <CheckCircle className="h-4 w-4 flex-shrink-0" />
        <h3>{title}</h3>
      </div>
      <p className="pl-7 text-sm text-secondary">{description}</p>
    </div>
  )
}

function TrustedBySection() {
  return (
    <div className="space-y-4">
      <p className="text-center text-xs text-secondary">
        Over $400m in payment volume secured for businesses like:
      </p>
      <div className="flex flex-wrap items-center justify-center gap-6">
        {partnerLogos.map((partner) => (
          <a
            key={partner.name}
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-70 transition-opacity hover:opacity-100"
          >
            <Image
              src={partner.src}
              alt={partner.name}
              width={100}
              height={32}
              className="h-6 w-auto dark:invert"
            />
          </a>
        ))}
      </div>
    </div>
  )
}

export function SignupPage() {
  const { referralCode, isInvalidCode } = useReferralCode()
  const isStandalone = useIsStandalone()

  return (
    <div className="flex w-full flex-col md:min-h-screen md:flex-row">
      {/* Left column - Onchain banking info, hidden on mobile */}
      <div className="hidden border-r border-gray-100/80 bg-gray-50 shadow-[inset_-4px_0_12px_-6px_rgba(0,0,0,0.04)] md:flex md:w-1/2 md:flex-col md:items-end md:justify-center md:p-12 md:pr-16 dark:border-transparent dark:bg-gray-900 dark:shadow-none">
        <div className="w-full max-w-sm space-y-10">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Image
              src="/logo_light.svg"
              alt="Splits logo"
              width={32}
              height={32}
              className="block dark:hidden"
            />
            <Image
              src="/logo_dark.svg"
              alt="Splits logo"
              width={32}
              height={32}
              className="hidden dark:block"
            />
            <span className="text-xl font-semibold text-gray-900 dark:text-white">
              Splits
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-semibold">Onchain banking</h1>

          {/* Features */}
          <div className="space-y-6">
            {features.map((feature) => (
              <FeatureItem
                key={feature.title}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>

          {/* Demo Video - desktop only */}
          <DesktopDemoVideoThumbnail />

          {/* Trusted By */}
          <TrustedBySection />
        </div>
      </div>

      {/* Right column - Sign up form, full width on mobile */}
      <div className="flex h-dvh w-full flex-col items-center justify-start overflow-auto bg-white px-5 pt-24 md:fixed md:right-0 md:top-0 md:h-screen md:w-1/2 md:items-start md:justify-center md:overflow-hidden md:p-12 md:pl-16 dark:bg-black">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo - only visible on mobile */}
          <div className="flex items-center gap-2 md:hidden">
            <Image
              src="/logo_light.svg"
              alt="Splits logo"
              width={40}
              height={40}
              className="block dark:hidden"
            />
            <Image
              src="/logo_dark.svg"
              alt="Splits logo"
              width={40}
              height={40}
              className="hidden dark:block"
            />
            <span className="text-xl font-semibold text-gray-900 dark:text-white">
              Splits
            </span>
          </div>

          <h2 className="text-balance text-left">
            {isStandalone
              ? 'Sign in to Splits'
              : 'Sign in or register for free'}
          </h2>

          {/* Referral messages */}
          {referralCode && (
            <Message variant="info">
              <p>
                <span className="font-medium">
                  {referralCode.referrerDisplayName ? (
                    <>
                      {referralCode.referrerDisplayName} (
                      {referralCode.referrerEmail})
                    </>
                  ) : (
                    referralCode.referrerEmail
                  )}
                </span>{' '}
                invited you to Splits. Sign in or register below to accept their
                invitation.
              </p>
            </Message>
          )}
          {isInvalidCode && (
            <Message variant="info">
              <p>Sign in or register below to start using Splits.</p>
            </Message>
          )}

          <div className="space-y-4">
            <LoginForm />
            {!isStandalone && (
              <p className="text-left text-sm text-secondary">
                Visit{' '}
                <SplitsLink
                  path="https://explorer.splits.org"
                  target="tab"
                  underline
                >
                  explorer.splits.org
                </SplitsLink>{' '}
                for the Splits Protocol
              </p>
            )}
          </div>
          {isStandalone && (
            <p className="text-sm text-secondary">
              Don&apos;t have an account? Open <strong>app.splits.org</strong>{' '}
              in your browser to create one.
            </p>
          )}
        </div>
      </div>

      {/* Demo Video - mobile floating thumbnail */}
      <div className="md:hidden">
        <MobileDemoVideoThumbnail />
      </div>
    </div>
  )
}
